import { Injectable } from "@angular/core";
import { CitronellaImg } from "../models/ExternalAPI/CitronellaImg";
import { CitronellaImgDataResponse } from "../models/ExternalAPI/CitronellaImgDataResponse";
import { environment } from "src/environments/environment";
import { LocalStorageService } from "./LocalStorage.service";
import { IndexedDBCacheService } from "./IndexedDBCache.service";
import { AzureStorageService } from "./ServiceCatalogEncapsulators/AzureStorage.service";
import { EnumFolderPaths } from "../helpers/enums/EnumFolderPaths";
import { UrlSasDTO } from "../models/AzureStorage/UrlSasDTO";
import { firstValueFrom } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ImageCacheService {
  dataDummy: CitronellaImgDataResponse = {
      Name: "1145_250526_122302.png",
      ContentType: "image/png",
      Md5: "PIWEmEsxOz00pVEhn+/N7g==",
      SignedUrl:
        "https://storage.googleapis.com/unilever.test.einteligent.com/1%2F1%2FTickets%2F1145%2F32a90073-0793-4b01-b5aa-77c844331b4d.png?GoogleAccessId=functions-hyperion@storage-test-253501.iam.gserviceaccount.com&Expires=1754786851&Signature=C7KXwZyi5hOE%2B7AVXj0cd4k5uBY8U40CRnwOs7mWVTQUbx%2Bzxh73bUKr6qwbup1bcZxj5ejRbuiWioyAPRkK%2FDdeDn5CPPBaDl3cPFrzeWJIfYa9O5fbXoRL008O2OLMJLqlpyxHmTPWlx4D0s6aiXRW%2Bm5dcUJgaTAeUqiPSuXcFtrOhfmhcJnItr68%2ByAY34zJD2kEJVNtd4gLIVgVILRL8reQBx94SsaLQujoBCuf%2FJD5uokOXY%2FeapCiWWNk3%2FNZm17QweOZUPFD7MByO8Ge0oA6Ls0Q6srXaKlCbkMAkyZdMtfaVpb81EpnNxbQiBsSpqYOVS8eCSb52L5C7A%3D%3D",
    };

  public isInitialized: boolean = false;
  private initPromise: Promise<void> | null = null;
  private isLoadingImages: boolean = false;

    
  public isReloading: boolean = false;
  public viewDesencriptado: any = [];

  private mapixImageMemoryCache: { [name: string]: string } = {};
  
  // Map para trackear renovaciones de URLs en progreso (evita race conditions)
  private urlRenewalInProgress: Map<string, Promise<string | null>> = new Map();
  
  private citronellaImageCache: CitronellaImg[] = [];

  constructor(
    private localStorageService: LocalStorageService,
    private indexedDBCache: IndexedDBCacheService,
    private azureStorageService: AzureStorageService,
  ) {
    this.initPromise = this.initializeMapixCacheToMemory();
  }

  async ensureInitialized(): Promise<void> {

    if (this.isInitialized) 
      return;
    
    if (this.initPromise) {
      await this.initPromise;
    }
  }

  private async initializeMapixCacheToMemory(): Promise<void> {
    try {
      var images = await this.indexedDBCache.getAllImages();

      if ((images == null || Object.keys(images).length === 0)) {
        await this.setAllMapixImagesOnDB();
        images = await this.indexedDBCache.getAllImages();
      }
      
      this.mapixImageMemoryCache = images;
      if (!environment.production) {
        const count = Object.keys(images).length;
        console.log(`Imágenes Mapix cargadas desde IndexedDB a memoria: ${count}`);
      }
    } catch (error) {
      console.error("Error cargando imágenes desde IndexedDB:", error);
      this.mapixImageMemoryCache = {};
    } finally {
      this.isInitialized = true;
      this.initPromise = null;
    }
  }

  async setAllMapixImagesOnDB(): Promise<void> {
    if (this.isLoadingImages) {
      console.log("Ya hay una carga en progreso");
      return;
    }

    this.isLoadingImages = true;

    try{
      const urlList = JSON.parse(this.localStorageService.getUrlList() || "[]");
      if (!urlList || urlList.length === 0) 
        return;
  
      const downloadPromises = urlList.map(async (item: { name: string; url: string }) => {
        
        const exists = await this.indexedDBCache.hasImage(item.name);
        if (exists) {
          if (!environment.production) console.log(`La imagen ${item.name} ya está en IndexedDB.`);
          return;
        }
  
        await this.downloadAndCacheImage(item.name, item.url, EnumFolderPaths.Location_Categories_Icons);
      });
  
      await Promise.all(downloadPromises);
      
      if (!environment.production) {
        const count = await this.indexedDBCache.getImageCount();
        console.log(`Imágenes en IndexedDB: ${count}, en memoria: ${Object.keys(this.mapixImageMemoryCache).length}`);
      }
    } finally{
      this.isLoadingImages = false;
    }
  }

  startBackgroundImageLoad(): void {
    
    this.ensureInitialized()
      .then(() => this.setAllMapixImagesOnDB())
      .then(() => {
        if (!environment.production) {
          console.log('se cargadron por detras, yay');
        }
      })
      .catch((error) => {
        console.error('Error en carga de imágenes en background:', error);
      });
  }

  private async downloadAndCacheImage(name: string, url: string, folderPath: EnumFolderPaths): Promise<void> {
    try {
      let currentUrl = url;
      let res = await fetch(currentUrl);
      
      //si la URL está expirada intentar renovarla
      if (!res.ok && (res.status === 403 || res.status === 404)) {
        if (!environment.production) console.log(`URL expirada para ${name}, renovando...`);
        
        const newUrl = await this.renewExpiredUrl(name, folderPath);
        if (newUrl) {
          currentUrl = newUrl;
          res = await fetch(currentUrl);
        }
      }

      if (!res.ok) {
        console.warn(`No se pudo descargar ${name}: ${res.status}`);
        return;
      }

      const blob = await res.blob();
      const dataUrl = await this.blobToBase64(blob);

      await this.indexedDBCache.saveImage(name, dataUrl);
      this.mapixImageMemoryCache[name] = dataUrl;
      
    } catch (error) {
      console.error(`Error descargando imagen ${name}:`, error);
    }
  }
  
  private async renewExpiredUrl(fileName: string, folderPath: EnumFolderPaths): Promise<string | null> {
    //si ya hay una renovación en progreso para esta imagen, esperar a que termine
    if (this.urlRenewalInProgress.has(fileName)) {
      if (!environment.production) console.log(`Renovación ya en progreso para ${fileName}, esperando...`);
      return this.urlRenewalInProgress.get(fileName)!;
    }

    //se crea la renovaicon de url y la guardo para que no s evaya a morir si por alguna razón se hace otra petición
    const renewalPromise = this.executeUrlRenewal(fileName, folderPath);
    this.urlRenewalInProgress.set(fileName, renewalPromise);

    try {
      const newUrl = await renewalPromise;
      return newUrl;
    } finally {
      this.urlRenewalInProgress.delete(fileName);
    }
  }

  private async executeUrlRenewal(fileName: string, folderPath: EnumFolderPaths): Promise<string | null> {
    try {
      const response = await firstValueFrom(
        this.azureStorageService.GetFileUrl(folderPath, fileName)
      );

      if (response?.isSuccess && response?.result?.url) {
        const newUrl = response.result.url;
        
        this.updateUrlInLocalStorage(fileName, newUrl);
        
        if (!environment.production) console.log(`URL renovada para ${fileName}`);
        return newUrl;
      } else {
        console.error(`No se obtuvo nueva URL para ${fileName}`, response);
        return null;
      }
    } catch (error) {
      console.error(`Error renovando URL para ${fileName}:`, error);
      return null;
    }
  }

  private updateUrlInLocalStorage(fileName: string, newUrl: string): void {
    try {
      const urlList: UrlSasDTO[] = JSON.parse(this.localStorageService.getUrlList() || "[]");
      const urlIndex = urlList.findIndex(item => item.name === fileName);
      
      if (urlIndex !== -1) {
        urlList[urlIndex].url = newUrl;
        this.localStorageService.setUrlList(JSON.stringify(urlList));
        if (!environment.production) console.log(`URL actualizada en localStorage para ${fileName}`);
      }
    } catch (error) {
      console.error(`Error actualizando URL en localStorage para ${fileName}:`, error);
    }
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  async setMapixImage(name: string, dataUrl: string): Promise<void> {
    try {
      await this.indexedDBCache.saveImage(name, dataUrl);
      
      this.mapixImageMemoryCache[name] = dataUrl;
    } catch (error) {
      console.error(`Error saving image ${name} to IndexedDB:`, error);
    }
  }

  async renewAndReloadImage(fileName: string, folderPath: EnumFolderPaths = EnumFolderPaths.Location_Categories_Icons): Promise<boolean> {
    try {
      const newUrl = await this.renewExpiredUrl(fileName, folderPath);
      if (!newUrl) {
        console.error(`No se pudo renovar URL para ${fileName}`);
        return false;
      }
      await this.downloadAndCacheImage(fileName, newUrl, folderPath);
      return true;
    } catch (error) {
      console.error(`Error renovando y recargando ${fileName}:`, error);
      return false;
    }
  }

  getMapixImage(name: string): string | undefined {
    return this.mapixImageMemoryCache[name];
  }

  async getMapixImageByDBAsync(name: string): Promise<string | null> {
    try {
      return await this.indexedDBCache.getImage(name);
    } catch (error) {
      console.error(`Error getting image ${name} from IndexedDB:`, error);
      return null;
    }
  }

  hasMapixImage(name: string): boolean {
    return !!this.mapixImageMemoryCache[name];
  }

  async hasMapixImageByDBAsync(name: string): Promise<boolean> {
    try {
      return await this.indexedDBCache.hasImage(name);
    } catch (error) {
      console.error(`Error checking image ${name} in IndexedDB:`, error);
      return false;
    }
  }
  
  isMapixImagesEmpty(): boolean {
    return Object.keys(this.mapixImageMemoryCache).length === 0;
  }
  
  async isMapixImagesEmptyByDBAsync(): Promise<boolean> {
    try {
      const count = await this.indexedDBCache.getImageCount();
      return count === 0;
    } catch (error) {
      console.error(`Error checking if IndexedDB is empty:`, error);
      return true;
    }
  }

  // citronella
  async setCitronellaImageBase64(citronellaImgData: CitronellaImgDataResponse, identifier: string) {
    try {
      const response = await fetch(citronellaImgData.SignedUrl, {
        method: "GET",
        headers: {
          "Content-Type": citronellaImgData.ContentType,
          "Content-MD5": citronellaImgData.Md5,
        }
      });
  
      const blob = await response.blob();
  
      const imageUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve(reader.result as string);
        };
        reader.onerror = () => {
          reject(reader.error);
        };
        reader.readAsDataURL(blob);
      });
  
      const citronellaImage: CitronellaImg = {
        Identifier: identifier,
        Name: citronellaImgData.Name,
        Url: imageUrl,
      };
      this.citronellaImageCache.push(citronellaImage);
    } catch (error) {
      console.error("Error cargando imagen:", error);
    }
  }

  getCitronellaImagesByIdentifier(identifier: string): string[] {
    var images = [];
    this.citronellaImageCache.forEach((cache) => {
      if (cache.Identifier === identifier) {
        images.push(cache.Url);
      }
    });
    // var images = this.citronellaImageCache.filter((img) => img.Identifier == identifier);
    if(!environment.production) console.log(`Obteniendo imag filter: ${identifier}`, this.citronellaImageCache, images);
    return images.length > 0 ? images : [];
  }

  hasCitronellaImageByIdentifier(identifier: string): boolean {
    return this.citronellaImageCache.some((img) => img.Identifier === identifier);
  }

  getCitronellaImageByName(name: string): string {
    const image = this.citronellaImageCache.find((img) => img.Name === name);
    return image ? image.Url : undefined;
  }

  hasCitronellaImageByName(name: string): boolean {
    return this.citronellaImageCache.some((img) => img.Name === name);
  }

  isCitronellaImagesEmpty(): boolean {
    return this.citronellaImageCache.length === 0;
  }

  emptyCitronellaImagesByIdentifier(identifier: string) {
    this.citronellaImageCache = this.citronellaImageCache.filter((img) => img.Identifier !== identifier);
  }


  // -----------
  async clearImageCache(): Promise<void> {
    this.citronellaImageCache = [];
    
    this.mapixImageMemoryCache = {};
    
    try {
      await this.indexedDBCache.clearCache();
      if (!environment.production) console.log("Cache limpiado: Citronella + Mapix (memoria + IndexedDB)");
    } catch (error) {
      console.error("Error clearing IndexedDB cache:", error);
    }
  }


  async getCacheStats(): Promise<{ mapixMemory: number; mapixPersistent: number; citronellaCount: number }> {
    let mapixPersistent = 0;
    try {
      mapixPersistent = await this.indexedDBCache.getImageCount();
    } catch (error) {
      console.error("Error getting IndexedDB count:", error);
    }
    return { 
      mapixMemory: Object.keys(this.mapixImageMemoryCache).length,
      mapixPersistent, 
      citronellaCount: this.citronellaImageCache.length 
    };
  }
}
